<script setup lang="ts">
import { computed, watch } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { serviceCompaniesApi, type ServiceCompany, type ServiceCompanyForm } from '@/api/service-companies';
import { useI18n } from 'vue-i18n';
import * as rutHelpers from 'rut-helpers';

const { t } = useI18n();
interface Props {
  serviceCompany?: ServiceCompany | null;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const queryClient = useQueryClient();

const isEditing = !!props.serviceCompany;
const title = computed(() => (isEditing ? 'Editar Empresa de Servicios' : 'Nueva Empresa de Servicios'));

const MIN_TAX_NAME_LENGTH = 2;
const MAX_TAX_NAME_LENGTH = 100;

const validationSchema = yup.object({
  taxName: yup
    .string()
    .required('El nombre de la empresa es requerido')
    .min(MIN_TAX_NAME_LENGTH, `El nombre debe tener al menos ${MIN_TAX_NAME_LENGTH} caracteres`)
    .max(MAX_TAX_NAME_LENGTH, `El nombre no puede exceder ${MAX_TAX_NAME_LENGTH} caracteres`),
  dni: yup
    .string()
    .required('El RUT es requerido')
    .test('valid-dni', t('caregiverForm.invalidDni'), (value) => (value ? rutHelpers.rutValidate(value) : true)),
  serviceType: yup
    .string()
    .required('El tipo de servicio es requerido'),
  receiptsEmail: yup
    .string()
    .email('Debe ser un email válido')
    .nullable(),
});

const { values, errors, defineField, handleSubmit, meta } = useForm<ServiceCompanyForm>({
  validationSchema,
  initialValues: {
    taxName: props.serviceCompany?.taxName || '',
    dni: props.serviceCompany?.dni || '',
    serviceType: props.serviceCompany?.serviceType || 'temporary_services',
    receiptsEmail: props.serviceCompany?.receiptsEmail || '',
  },
});

const [taxName, taxNameAttrs] = defineField('taxName');
const [dni, dniAttrs] = defineField('dni');
const [serviceType, serviceTypeAttrs] = defineField('serviceType');
const [receiptsEmail, receiptsEmailAttrs] = defineField('receiptsEmail');

const serviceTypeOptions = [
  { value: 'temporary_services', title: 'Servicios Temporales' },
  { value: 'contractor', title: 'Contratista' },
  { value: 'subcontractor', title: 'Subcontratista' },
];

watch(dni, (value) => {
  dni.value = rutHelpers.rutFormat(value);
}, { immediate: true });

const createMutation = useMutation({
  mutationFn: () => serviceCompaniesApi.create(values),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['serviceCompanies'] });
    emit('success');
    emit('close');
  },
});

const updateMutation = useMutation({
  mutationFn: () => serviceCompaniesApi.update({ ...values, id: props.serviceCompany!.id }),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['serviceCompanies'] });
    emit('success');
    emit('close');
  },
});

const isLoading = computed(() => createMutation.isPending.value || updateMutation.isPending.value);

const onSubmit = handleSubmit(() => {
  if (isEditing && props.serviceCompany) {
    updateMutation.mutate();
  } else {
    createMutation.mutate();
  }
});

function handleClose() {
  emit('close');
}
</script>
<template>
  <v-card>
    <v-card-title>{{ title }}</v-card-title>
    <v-card-text>
      <form @submit.prevent="onSubmit">
        <v-text-field
          v-model="taxName"
          label="Nombre de la empresa"
          :error-messages="errors.taxName"
          :disabled="isLoading"
          variant="outlined"
          class="mb-4"
          v-bind="taxNameAttrs"
        />

        <v-text-field
          v-model="dni"
          label="RUT"
          :error-messages="errors.dni"
          :disabled="isLoading"
          variant="outlined"
          class="mb-4"
          v-bind="dniAttrs"
        />

        <v-select
          v-model="serviceType"
          :items="serviceTypeOptions"
          item-title="title"
          item-value="value"
          label="Tipo de Servicio"
          :error-messages="errors.serviceType"
          :disabled="isLoading"
          variant="outlined"
          class="mb-4"
          v-bind="serviceTypeAttrs"
        />

        <v-text-field
          v-model="receiptsEmail"
          label="Email para Recibos (Opcional)"
          :error-messages="errors.receiptsEmail"
          :disabled="isLoading"
          variant="outlined"
          class="mb-4"
          type="email"
          v-bind="receiptsEmailAttrs"
        />
      </form>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="isLoading"
        @click="handleClose"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        :disabled="!meta.valid"
        :loading="isLoading"
        @click="onSubmit"
      >
        {{ isEditing ? 'Actualizar' : 'Crear' }}
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="createMutation.isError.value || updateMutation.isError.value"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      Ha ocurrido un error. Intenta nuevamente. Recuerda que el nombre no puede repetirse.
    </v-alert>
  </v-card>
</template>
